/**
 * Number conversion utilities following satoshi scale (1e8 = 100_000_000)
 * Consistent with Go implementation: safem.SatoshiScale
 * 
 * Following morphcore precision standards: 8 decimal places (satoshi)
 * Example: "1.0" -> 100000000, "1.5" -> 150000000
 */

/**
 * Satoshi scale constant: 1e8 (8 decimal places)
 * Following morphcore precision standards
 * Matches: safem.SatoshiScale in Go codebase
 */
export const SATOSHI_SCALE = 100_000_000;

/**
 * Converts a decimal value to satoshi-scaled string
 * Used for API requests where values must be in satoshi format
 * 
 * @param value - Decimal number (e.g., 1.5) or string representation
 * @returns Satoshi-scaled string (e.g., "150000000")
 * @throws Error if value is invalid or negative
 * 
 * @example
 * toSatoshi(1.0) // "100000000"
 * toSatoshi("1.5") // "150000000"
 * toSatoshi(60000) // "6000000000000"
 */
export function toSatoshi(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    throw new Error(`Invalid value for satoshi conversion: ${value}`);
  }
  
  if (num < 0) {
    throw new Error(`Negative values not allowed for satoshi conversion: ${value}`);
  }
  
  // Use BigInt for precision with large numbers
  // Multiply by scale and convert to string
  const scaled = BigInt(Math.floor(num * SATOSHI_SCALE));
  return scaled.toString();
}

/**
 * Converts a satoshi-scaled string to decimal number
 * Used for displaying API responses in human-readable format
 * 
 * @param satoshi - Satoshi-scaled string (e.g., "150000000")
 * @returns Decimal number (e.g., 1.5)
 * 
 * @example
 * fromSatoshi("100000000") // 1.0
 * fromSatoshi("150000000") // 1.5
 * fromSatoshi("6000000000000") // 60000.0
 * fromSatoshi("64999999999986700000000") // 649999999999867.0 (preserves precision)
 */
export function fromSatoshi(satoshi: string): number {
  const num = BigInt(satoshi);
  const scale = BigInt(SATOSHI_SCALE);
  // Divide in BigInt first to preserve precision, then convert to Number
  // This prevents precision loss for very large values
  const quotient = num / scale;
  const remainder = num % scale;
  // Convert to decimal: quotient + (remainder / scale)
  return Number(quotient) + Number(remainder) / SATOSHI_SCALE;
}

/**
 * Converts a decimal value to satoshi-scaled number (uint64 equivalent)
 * Note: JavaScript numbers are safe up to 2^53-1, so use string for very large values
 * 
 * @param value - Decimal number
 * @returns Satoshi-scaled number (may lose precision for very large values)
 * 
 * @example
 * toSatoshiUint64(1.0) // 100000000
 * toSatoshiUint64(1.5) // 150000000
 */
export function toSatoshiUint64(value: number): number {
  if (value < 0) {
    throw new Error(`Negative values not allowed: ${value}`);
  }
  return Math.floor(value * SATOSHI_SCALE);
}

/**
 * Validates if a string is a valid satoshi-scaled value
 * 
 * @param value - String to validate
 * @returns true if valid satoshi format, false otherwise
 * 
 * @example
 * isValidSatoshi("100000000") // true
 * isValidSatoshi("abc") // false
 * isValidSatoshi("-100") // false
 */
export function isValidSatoshi(value: string): boolean {
  try {
    const num = BigInt(value);
    return num >= 0n;
  } catch {
    return false;
  }
}

/**
 * Converts multiple decimal values to satoshi strings
 * Useful for batch operations
 * 
 * @param values - Array of decimal numbers or strings
 * @returns Array of satoshi-scaled strings
 */
export function toSatoshiBatch(values: (number | string)[]): string[] {
  return values.map(toSatoshi);
}

/**
 * Converts multiple satoshi strings to decimal numbers
 * Useful for batch display operations
 * 
 * @param satoshis - Array of satoshi-scaled strings (numbers will be converted to strings)
 * @returns Array of decimal numbers
 */
export function fromSatoshiBatch(satoshis: (string | number)[]): number[] {
  return satoshis.map((val) => fromSatoshi(typeof val === 'string' ? val : String(val)));
}

/**
 * Convert number to hex string with 0x prefix
 * Used for EIP-712 message fields that require hex format (nonce, deadline)
 * 
 * @param value - Number or string representation of number
 * @returns Hex string with 0x prefix (e.g., "0x1", "0xff")
 * 
 * @example
 * int64ToHex(1) // "0x1"
 * int64ToHex(255) // "0xff"
 * int64ToHex("10") // "0xa"
 */
export function int64ToHex(value: number | string): string {
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  return `0x${num.toString(16)}`;
}

// ============================================================================
// BUCKET DATA CONVERSION UTILITIES
// ============================================================================

/**
 * Convert satoshi string/number to decimal number using fromSatoshi
 * Handles undefined, null, empty strings, and invalid values safely
 * Used for converting API response fields that are in satoshi format
 * 
 * @param val - Value to convert (string, number, undefined, null, or empty string)
 * @returns Decimal number (0 if invalid)
 * 
 * @example
 * toNumberFromSatoshi("100000000") // 1.0
 * toNumberFromSatoshi(undefined) // 0
 * toNumberFromSatoshi("150000000") // 1.5
 */
export function toNumberFromSatoshi(val: any): number {
  if (val === undefined || val === null || val === "") return 0;
  // Convert to string before calling fromSatoshi (fromSatoshi only accepts strings)
  if (typeof val === "string" || typeof val === "number") {
    try {
      return fromSatoshi(typeof val === "string" ? val : String(val));
    } catch {
      return 0;
    }
  }
  return 0;
}

/**
 * Convert string percentage or number to decimal number
 * Used for values like IMR (Initial Margin Requirement) and leverage that are not satoshi-scaled
 * 
 * @param val - Value to convert (string, number, undefined, null, or empty string)
 * @returns Decimal number (0 if invalid)
 * 
 * @example
 * parseNumericValue("10") // 10
 * parseNumericValue("10.5") // 10.5
 * parseNumericValue(20) // 20
 * parseNumericValue(undefined) // 0
 */
export function parseNumericValue(val: any): number {
  if (val === undefined || val === null || val === "") return 0;
  try {
    if (typeof val === "string") {
      const parsed = parseFloat(val);
      return isNaN(parsed) ? 0 : parsed;
    }
    if (typeof val === "number") {
      return isNaN(val) ? 0 : val;
    }
    return 0;
  } catch {
    return 0;
  }
}

/**
 * Convert satoshi string/number to BigInt for precise arithmetic operations
 * Used for calculations that need to preserve precision during subtraction/addition
 * This preserves precision during the arithmetic operation before converting to decimal
 * 
 * @param val - Value to convert (string, number, undefined, null, or empty string)
 * @returns BigInt value (0n if invalid)
 * 
 * @example
 * toBigInt("100000000") // 100000000n
 * toBigInt(100000000) // 100000000n
 * toBigInt(undefined) // 0n
 */
export function toBigInt(val: any): bigint {
  if (val === undefined || val === null || val === "") return BigInt(0);
  try {
    // Convert to string first to handle both string and number inputs consistently
    // BigInt constructor accepts string representation of numbers
    const str = typeof val === "string" ? val : String(val);
    // Validate it's a valid number string (not empty, not just whitespace)
    if (str.trim() === "" || isNaN(Number(str))) {
      return BigInt(0);
    }
    return BigInt(str);
  } catch {
    return BigInt(0);
  }
}

/**
 * Formats a number to show up to 6 decimal places, removing trailing zeros
 * Used for displaying size input values in trading interfaces
 * 
 * @param value - Number to format
 * @returns Formatted string with up to 6 decimal places (empty string if value is 0)
 * 
 * @example
 * formatSizeInputValue(1.5) // "1.5"
 * formatSizeInputValue(1.123456) // "1.123456"
 * formatSizeInputValue(1.123456789) // "1.123457" (rounded to 6 decimals)
 * formatSizeInputValue(0) // ""
 * formatSizeInputValue(1.0) // "1"
 */
export function formatSizeInputValue(value: number): string {
  if (value === 0) return "";
  // Format to 6 decimal places and remove trailing zeros for cleaner display
  return value.toFixed(6).replace(/\.?0+$/, "");
}
